<script setup lang="ts">
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import RecordsWeeklyChart from '@/components/charts/RecordsWeeklyChart.vue';
import RecordsTable from '@/components/records/RecordsTable.vue';
import BulkCreateButton from '@/components/records/BulkCreateButton.vue';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu } from '@/components/ui/data-table';
import colors from '@/plugins/tailwind';
import { BULK_CREATE, useTransfersPage } from './useTransfersPage';

/*
 * This page holds its own data rather than delegating it to a single child, which is the exception
 * here. The chart and the table read the same fetch, one way: the chart bins every loaded row and
 * stays that way, the table shows the subset the picked week and the header facets leave, and the
 * totals cover that same subset. Whichever child owned the fetch, the other would have to reach
 * across to it. All of the logic lives in the shared records composables beside the page. The
 * column-visibility control rides in `RecordsTable`'s own `#actions` slot, beside its heading, the
 * same way Slaughters'/Status's does.
 *
 * A move whose destination is also in the current selection appears on the Insertions page too,
 * from the receiving end. The two pages anchor on opposite ends of the same registration pair, so
 * that is the intended reading, not a duplicate.
 */
const {
    bins,
    columnFilters,
    columns,
    columnToggleOptions,
    facetOptions,
    isError,
    isFetching,
    refetch,
    refreshAfterFormActivity,
    resetSelection,
    selectWeek,
    selectedWeekStart,
    setAllColumns,
    sorting,
    subtitle,
    toggleColumn,
    totals,
    visibleRows,
} = useTransfersPage();
</script>

<template>
    <DashboardContentLayout class="space-y-8" :heading="$t('views.pigs.transfers.header.heading')" :has-period-picker="true" :has-pig-type-picker="true">
        <div v-if="isError" class="py-14 text-center px-4 text-gray-500 text-lg space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <div v-else class="space-y-8">
            <MainViewCard class="py-4 px-2">
                <RecordsWeeklyChart
                    :bins="bins"
                    :selected-week-start="selectedWeekStart"
                    :color="colors.chart.transfer"
                    :title="$t('views.pigs.transfers.chart.heading')"
                    @week-selected="selectWeek"
                    @selection-reset="resetSelection"
                />
            </MainViewCard>
            <MainViewCard class="overflow-hidden">
                <RecordsTable
                    v-model:sorting="sorting"
                    v-model:column-filters="columnFilters"
                    :columns="columns"
                    :rows="visibleRows"
                    :totals="totals"
                    :facet-options="facetOptions"
                    :loading="isFetching"
                    :heading="$t('views.pigs.transfers.table.heading')"
                    :subtitle="subtitle"
                    @form-activity="refreshAfterFormActivity"
                >
                    <template #actions>
                        <div class="flex items-center gap-2">
                            <BulkCreateButton :form-id="BULK_CREATE.formId" :label="$t(BULK_CREATE.labelKey)" :location-step="BULK_CREATE.locationStep" @form-activity="refreshAfterFormActivity" />
                            <ColumnVisibilityMenu
                                :options="columnToggleOptions"
                                :label="$t('views.pigs.transfers.table.columnsButton')"
                                @toggle="toggleColumn"
                                @select-all="setAllColumns(true)"
                                @deselect-all="setAllColumns(false)"
                            />
                        </div>
                    </template>
                </RecordsTable>
            </MainViewCard>
        </div>
    </DashboardContentLayout>
</template>
